import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import Section from '../components/Section';
import { SelectField, TextArea, TextField } from '../components/FormField';
import useAuth from '../auth/useAuth';
import { listPlants } from '../services/plants';
import { listSuppliers, getRateFor } from '../services/suppliers';
import { listTeam } from '../services/team';
import { formatCurrency } from '../utils/formatters';
import {
  ADJUSTMENT_TYPES,
  BILLING_DISPOSITION_LABELS,
  CREDIT_DISPOSITIONS,
  SUPPLEMENT_DISPOSITIONS,
  createAdjustmentTimesheet,
  weekStartFromDate,
} from '../services/timesheets';

/**
 * Adjustments tab (Billing Tracker → Adjustments).
 *
 * A standalone entry point for issuing a Supplement (extra dollars owed
 * by the customer) or a Credit (dollars to refund) WITHOUT first hunting
 * down the original weekly timesheet. The Director picks the plant and
 * supplier directly, chooses the adjustment type, and enters a flat
 * dollar amount, a reason, and a billing disposition. The adjustment
 * dates to the day it's raised (the Date of Adjustment field was removed
 * Aug 2026), which sets its billing period.
 *
 * The adjustment is created as a Pending record so it flows through the
 * normal Approvals queue; once approved it's picked up by the invoice
 * draft for whichever billing period that date falls in — the
 * same routing the old per-timesheet "Add Supplement" / "Issue Credit"
 * buttons used, just reached from one central place.
 */
export default function Adjustments() {
  const { isDirector, isRegionalManager, name, email } = useAuth();
  const navigate = useNavigate();
  const canCreate = isDirector || isRegionalManager;

  const [plants, setPlants] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state.
  const [type, setType] = useState(ADJUSTMENT_TYPES.SUPPLEMENT);
  const [plantCode, setPlantCode] = useState('');
  const [supplierCode, setSupplierCode] = useState('');
  // "Bill By" mode (per Jillian, July 2026): a flat dollar amount, or a
  // number of hours priced on the invoice at the supplier's rate for the
  // plant. Defaults to dollars so the tab behaves as before unless the
  // creator opts into hours.
  const [billMode, setBillMode] = useState('dollars'); // 'dollars' | 'hours'
  const [amount, setAmount] = useState('');
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [disposition, setDisposition] = useState('');
  // Date of Adjustment (re-added per Jillian, Oct 2026). Defaults to today
  // and may be moved back to any earlier day in the current calendar year,
  // never forward. It drives which billing period the adjustment lands in
  // (the Sunday-anchored WeekStart), the rate lookup in hours mode, and
  // the title.
  const todayIso = useMemo(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${dd}`;
  }, []);
  const yearStartIso = `${todayIso.slice(0, 4)}-01-01`;
  const [adjustmentDate, setAdjustmentDate] = useState(todayIso);
  const dateIso = /^\d{4}-\d{2}-\d{2}$/.test(adjustmentDate) ? adjustmentDate : todayIso;

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [plantRows, supplierRows, teamRows] = await Promise.all([
          listPlants(),
          listSuppliers(),
          listTeam(),
        ]);
        if (cancelled) return;
        setPlants(plantRows || []);
        setSuppliers(supplierRows || []);
        setTeam(teamRows || []);
      } catch {
        if (cancelled) return;
        setPlants([]);
        setSuppliers([]);
        setTeam([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isCredit = type === ADJUSTMENT_TYPES.CREDIT;

  // Disposition options swap with the adjustment type so a supplement
  // can't be saved with a credit disposition (or vice versa).
  const dispositionOptions = useMemo(() => {
    const keys = isCredit ? CREDIT_DISPOSITIONS : SUPPLEMENT_DISPOSITIONS;
    return keys.map((value) => ({
      value,
      label: BILLING_DISPOSITION_LABELS[value] || value,
    }));
  }, [isCredit]);

  // Clear the chosen disposition whenever the type flips so a stale
  // selection from the other type can't sneak through.
  const onTypeChange = (next) => {
    setType(next);
    setDisposition('');
    setErrors((e) => ({ ...e, disposition: undefined }));
  };

  // The signed-in user's own plant codes (from their Team record).
  // Directors are exempt from scoping; everyone else (Regional Managers
  // here, since canCreate is Director||RM) can only issue adjustments
  // for their own plants and the suppliers at those plants.
  const myPlantCodes = useMemo(() => {
    const normEmail = (email || '').toLowerCase();
    const meRec = (team || []).find(
      (m) => (m.Email || '').toLowerCase() === normEmail,
    );
    const codes = Array.isArray(meRec?.plantCodes) ? meRec.plantCodes : [];
    return new Set(codes.map((c) => String(c || '').toUpperCase()));
  }, [team, email]);

  const plantOptions = useMemo(
    () =>
      (plants || [])
        .filter((p) => p.PlantCode)
        .filter(
          (p) =>
            isDirector ||
            myPlantCodes.has(String(p.PlantCode).toUpperCase()),
        )
        .map((p) => ({
          value: p.PlantCode,
          label: p.Title ? `${p.PlantCode} — ${p.Title}` : p.PlantCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [plants, isDirector, myPlantCodes],
  );
  const supplierOptions = useMemo(
    () =>
      (suppliers || [])
        .filter((s) => s.SupplierCode)
        .filter((s) => {
          if (isDirector) return true;
          // Non-Directors only see suppliers that have a plant
          // assignment at one of their own plants.
          return (s.plantAssignments || []).some((pa) =>
            myPlantCodes.has(String(pa.plantCode || '').toUpperCase()),
          );
        })
        .map((s) => ({
          value: s.SupplierCode,
          // Sorted by supplier NAME (not account number) so the list
          // reads A–Z the way people think of suppliers (Oct 2026).
          label: s.Title ? `${s.SupplierCode} — ${s.Title}` : s.SupplierCode,
          sortKey: s.Title || s.SupplierCode,
        }))
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { sensitivity: 'base' })),
    [suppliers, isDirector, myPlantCodes],
  );

  const validate = () => {
    const e = {};
    if (!plantCode) e.plantCode = 'Required';
    if (!supplierCode) e.supplierCode = 'Required';
    // Value check depends on the Bill By mode: dollars → a positive
    // amount; hours → a positive number of hours (the rate comes from
    // the supplier on the invoice).
    if (billMode === 'hours') {
      const h = Number(hours);
      if (hours === '' || !Number.isFinite(h) || h <= 0) {
        e.hours = 'Enter a number of hours greater than 0.';
      }
    } else {
      const amt = Number(amount);
      if (amount === '' || !Number.isFinite(amt) || amt <= 0) {
        e.amount = 'Enter an amount greater than 0.';
      }
    }
    if (!reason.trim()) e.reason = 'Required';
    if (!disposition) e.disposition = 'Required';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(adjustmentDate)) {
      e.adjustmentDate = 'Pick a date.';
    } else if (adjustmentDate > todayIso) {
      e.adjustmentDate = 'The date cannot be in the future.';
    } else if (adjustmentDate < yearStartIso) {
      e.adjustmentDate = `Pick a date in ${todayIso.slice(0, 4)}.`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setSaveError('');
    if (!validate()) return;
    setSaving(true);
    try {
      const amt = Number(amount);
      const hrs = Number(hours);
      const weekStart = weekStartFromDate(dateIso);
      // In hours mode we send AdjustmentHours and NO dollar amount — the
      // invoice prices it at the supplier's rate. In dollars mode we send
      // the flat CreditAmount / SupplementAmount as before.
      const valueFields =
        billMode === 'hours'
          ? { AdjustmentHours: hrs }
          : isCredit
            ? { CreditAmount: amt }
            : { SupplementAmount: amt };
      await createAdjustmentTimesheet({
        rows: [],
        AdjustmentType: type,
        AdjustmentSupplierCode: supplierCode,
        AdjustmentPlantCode: plantCode,
        AdjustmentReason: reason.trim(),
        BillingDisposition: disposition,
        ...valueFields,
        // The chosen Date of Adjustment drives which billing period it
        // lands in — stored as the Sunday-anchored WeekStart the invoice
        // generator already filters on.
        WeekStart: weekStart,
        AdjustmentDate: dateIso,
        // Stamp the creator so the Approvals queue shows who raised it.
        SubmitterName: name || email || '',
        SubmitterEmail: email || '',
        Title: `${isCredit ? 'Credit' : 'Supplement'} — ${supplierCode} @ ${plantCode} — ${dateIso}`,
      });
      navigate('/timesheets/approvals');
    } catch (err) {
      setSaveError(err?.message || 'Something went wrong saving this adjustment.');
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  if (!canCreate) {
    return (
      <div className="card">
        <div className="card-title">Adjustments</div>
        <p className="muted">
          Only a Regional Manager or Director can create billing adjustments.
        </p>
      </div>
    );
  }

  const amountLabel = isCredit ? 'Credit Amount (USD) *' : 'Supplement Amount (USD) *';
  const reasonLabel = isCredit ? 'Reason for Credit *' : 'Reason for Supplement *';

  // Live rate preview for hours mode — resolves the selected supplier's
  // rate for the chosen plant, effective the adjustment date, and shows
  // the resulting dollar figure so the creator can sanity-check it before
  // saving. The invoice does the same lookup at generation time.
  const previewSupplier = supplierCode
    ? suppliers.find(
        (s) =>
          String(s.SupplierCode || '').toUpperCase() ===
          String(supplierCode).toUpperCase(),
      )
    : null;
  const previewRate =
    billMode === 'hours' && previewSupplier && plantCode
      ? getRateFor(previewSupplier, plantCode, dateIso)
      : null;
  const previewHours = Number(hours);
  const previewDollars =
    previewRate != null && Number.isFinite(previewHours) && previewHours > 0
      ? previewHours * previewRate
      : null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>New Adjustment</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Issue a supplement or a credit for a supplier and plant. It lands in
            the Approvals queue and, once approved, is picked up on that
            supplier's next invoice or issued as a supplemental invoice.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <Section title="Adjustment">
          <div className="form-grid">
            <SelectField
              label="Type *"
              value={type}
              onChange={onTypeChange}
              options={[
                { value: ADJUSTMENT_TYPES.SUPPLEMENT, label: 'Add Supplement' },
                { value: ADJUSTMENT_TYPES.CREDIT, label: 'Issue Credit' },
              ]}
              placeholder=""
            />
            {/* Date of Adjustment — any day this calendar year up to today
                (per Jillian, Oct 2026). Decides the billing period. */}
            <TextField
              label="Date of Adjustment *"
              type="date"
              value={adjustmentDate}
              onChange={(v) => {
                setAdjustmentDate(v);
                setErrors((e) => ({ ...e, adjustmentDate: undefined }));
              }}
              min={yearStartIso}
              max={todayIso}
              error={errors.adjustmentDate}
            />
            <SelectField
              label="Plant *"
              value={plantCode}
              onChange={(v) => setPlantCode(v)}
              options={plantOptions}
              error={errors.plantCode}
            />
            <SelectField
              label="Supplier *"
              value={supplierCode}
              onChange={(v) => setSupplierCode(v)}
              options={supplierOptions}
              error={errors.supplierCode}
            />
            {/* Bill By — flat dollar amount (as before) or hours priced
                at the supplier's rate on the invoice (per Jillian, July
                2026). */}
            <SelectField
              label="Bill By *"
              value={billMode}
              onChange={(v) => setBillMode(v)}
              options={[
                { value: 'dollars', label: 'Flat Amount ($)' },
                { value: 'hours', label: 'Hours (At Supplier Rate)' },
              ]}
              placeholder=""
            />
            {billMode === 'hours' ? (
              <TextField
                label={isCredit ? 'Credit Hours *' : 'Supplement Hours *'}
                type="number"
                step="0.25"
                min="0"
                value={hours}
                onChange={setHours}
                placeholder="4"
                error={errors.hours}
              />
            ) : (
              <TextField
                label={amountLabel}
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={setAmount}
                placeholder="450.00"
                error={errors.amount}
              />
            )}
            {/* Live rate preview — only in hours mode. Shows what the
                invoice will compute (hours × the supplier's rate for the
                plant), or a heads-up when no rate is on file yet. */}
            {billMode === 'hours' && (
              <div className="field full">
                {previewRate == null ? (
                  <span className="muted" style={{ fontSize: 12 }}>
                    {supplierCode && plantCode
                      ? 'No rate on file for this supplier at this plant yet — set the hourly rate on the supplier’s page, or the invoice will flag this line.'
                      : 'Pick a plant and supplier to see the rate that will be applied.'}
                  </span>
                ) : (
                  <span style={{ fontSize: 13 }}>
                    Rate on file: <strong>{formatCurrency(previewRate)}/hr</strong>
                    {previewDollars != null && (
                      <>
                        {' '}·{' '}
                        {previewHours} hrs ={' '}
                        <strong>
                          {isCredit ? '−' : ''}
                          {formatCurrency(previewDollars)}
                        </strong>{' '}
                        on the invoice
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
            <SelectField
              label="Billing Disposition *"
              value={disposition}
              onChange={(v) => setDisposition(v)}
              options={dispositionOptions}
              error={errors.disposition}
              full
            />
            <TextArea
              label={reasonLabel}
              value={reason}
              onChange={setReason}
              rows={3}
              maxLength={200}
              error={errors.reason}
            />
            <div className="field full">
              <span className="muted" style={{ fontSize: 11 }}>
                The reason prints as the line description on the invoice — keep
                it short and specific.
              </span>
            </div>
          </div>
        </Section>

        {saveError && (
          <div
            style={{
              margin: '12px 0',
              padding: '10px 12px',
              background: '#fde8e8',
              color: '#b91c1c',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <strong>Couldn't save:</strong> {saveError}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate('/timesheets/approvals')}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Create Adjustment'}
          </button>
        </div>
      </form>
    </>
  );
}
